import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Home,
  MessageSquare,
  FileText,
  BarChart3,
  Workflow,
  LayoutDashboard,
  Settings,
  ArrowLeftRight,
  LogOut,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Separator } from '@/components/ui/separator';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useAuth } from '@/auth/AuthContext';
import { CompanySwitcher } from '@/components/auth/CompanySwitcher';
import { FEATURES } from '@/config/features';
import type { AITool } from './types';

interface IconRailProps {
  activeTool: AITool;
  onToolChange: (tool: AITool) => void;
}

const tools: { id: AITool; icon: typeof MessageSquare; label: string }[] = [
  { id: 'chat', icon: MessageSquare, label: 'AI Chat' },
  { id: 'reports', icon: BarChart3, label: 'Reports' },
  ...(FEATURES.documents
    ? [{ id: 'documents' as AITool, icon: FileText, label: 'Documents' }]
    : []),
  { id: 'workflows', icon: Workflow, label: 'Workflows' },
  { id: 'dashboard', icon: LayoutDashboard, label: 'Dashboards' },
];

function avatarInitials(
  firstname?: string | null,
  lastname?: string | null,
  email?: string
): string {
  if (firstname?.trim() || lastname?.trim()) {
    const f = (firstname?.trim() ?? '').charAt(0).toUpperCase();
    const l = (lastname?.trim() ?? '').charAt(0).toUpperCase();
    return ((f + l).trim() || email?.charAt(0)?.toUpperCase()) ?? '?';
  }
  return email?.charAt(0)?.toUpperCase() ?? '?';
}

export function IconRail({ activeTool, onToolChange }: IconRailProps) {
  const { user, selectedEmployer } = useAuth();
  const navigate = useNavigate();
  const [companyOpen, setCompanyOpen] = useState(false);
  const employerLabel =
    selectedEmployer?.shortname ?? selectedEmployer?.name ?? selectedEmployer?.code ?? null;
  const displayName =
    [user?.firstname, user?.lastname].filter(Boolean).join(' ') || user?.email || 'Account';

  return (
    <TooltipProvider delayDuration={0}>
      <div className="flex h-full w-12 flex-col items-center border-r bg-muted/50 py-2">
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              onClick={() => onToolChange('home')}
              className={cn(
                'flex h-10 w-10 items-center justify-center rounded-lg transition-colors',
                activeTool === 'home'
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
              )}
            >
              <Home className="h-5 w-5" />
            </button>
          </TooltipTrigger>
          <TooltipContent side="right" sideOffset={8}>
            Home
          </TooltipContent>
        </Tooltip>

        <Separator className="my-2 w-6" />

        <div className="flex flex-1 flex-col gap-1">
          {tools.map(({ id, icon: Icon, label }) => (
            <Tooltip key={id}>
              <TooltipTrigger asChild>
                <button
                  onClick={() => onToolChange(id)}
                  className={cn(
                    'flex h-10 w-10 items-center justify-center rounded-lg transition-colors',
                    activeTool === id
                      ? 'bg-primary text-primary-foreground'
                      : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                  )}
                >
                  <Icon className="h-5 w-5" />
                </button>
              </TooltipTrigger>
              <TooltipContent side="right" sideOffset={8}>
                {label}
              </TooltipContent>
            </Tooltip>
          ))}
        </div>

        <div className="flex flex-col items-center gap-1">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary text-xs font-medium text-primary-foreground outline-none ring-offset-background focus-visible:ring-2 focus-visible:ring-ring"
                title={displayName}
              >
                {avatarInitials(user?.firstname, user?.lastname, user?.email)}
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent side="right" align="end" sideOffset={10} className="w-56">
              <DropdownMenuLabel className="font-normal">
                <div className="flex flex-col space-y-1">
                  <p className="truncate text-sm font-medium leading-none">{displayName}</p>
                  <p className="truncate text-xs leading-none text-muted-foreground">
                    {user?.email}
                  </p>
                  {employerLabel && (
                    <p className="truncate pt-1 text-xs text-muted-foreground">{employerLabel}</p>
                  )}
                </div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => setCompanyOpen(true)}>
                <ArrowLeftRight className="mr-2 h-4 w-4" />
                Switch company
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => onToolChange('settings')}>
                <Settings className="mr-2 h-4 w-4" />
                Settings
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => navigate('/logout')}>
                <LogOut className="mr-2 h-4 w-4" />
                Sign out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          {employerLabel && (
            <span
              className="max-w-[2.5rem] truncate text-[10px] text-muted-foreground"
              title={selectedEmployer?.name ?? employerLabel}
            >
              {employerLabel}
            </span>
          )}
        </div>

        <Tooltip>
          <TooltipTrigger asChild>
            <button
              onClick={() => onToolChange('settings')}
              className={cn(
                'flex h-10 w-10 items-center justify-center rounded-lg transition-colors',
                activeTool === 'settings'
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
              )}
            >
              <Settings className="h-5 w-5" />
            </button>
          </TooltipTrigger>
          <TooltipContent side="right" sideOffset={8}>
            Settings
          </TooltipContent>
        </Tooltip>
      </div>

      <CompanySwitcher open={companyOpen} onOpenChange={setCompanyOpen} />
    </TooltipProvider>
  );
}
